import { useEffect, useRef, useState } from 'react'
import { NavLink, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import {
  Breadcrumbs,
  Container,
  Link,
  Stepper,
  Step,
  Typography,
  useMediaQuery,
  useTheme,
  StepButton,
  Card,
  CardHeader,
  CardContent,
  List,
  TextField,
  ListItemText,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemButton,
  Stack,
  AlertTitle,
  CardActions,
  Alert,
  Box,
  Grid,
  ListItemSecondaryAction,
  Tooltip
} from '@mui/material'
import PageHeader from '../components/PageHeader'
import SelectProjectStep from '../components/installation/SelectProjectStep'
import ScanQrCodeOrEnterDeviceCodeStep from '../components/installation/ScanQrCodeOrEnterDeviceCodeStep'
import LevelSelect from '../components/LevelSelect'
import LiftSelect from '../components/LiftSelect'
import { Check, ExpandMore, Warning } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Asset, AssetInput, Device, Heater, HeaterInput, Project, RuuviTag, Valve } from '../types'
import DeviceType from '../enums/DeviceType'
import RuuviTagType from '../enums/RuuviTagType'
import { getDevice, getPairedRuuviTag, invokeAction } from '../services/deviceService'
import LoadingState from '../components/LoadingState'
import { getProject } from '../services/projectService'
import { createAsset, getAsset, updateAsset, uploadAssetPhoto } from '../services/assetService'
import { FormikHelpers, useFormik } from 'formik'
import { AssetType } from '../enums/AssetType'
import HeaterDetailsList from '../components/HeaterDetailsList'
import HeaterAutocomplete from '../components/HeaterAutocomplete'
import HeaterDetachAlert from '../components/HeaterDetachAlert'
import ValveDetachInRuuviTagAlert from '../components/ValveDetachInRuuviTagAlert'
import RuuviTagDetailsList from '../components/RuuviTagDetailsList'
import { getRuuviTag } from '../services/ruuviTagService'
import RuuviTagDetachAlert from '../components/RuuviTagDetachAlert'
import ValveDetachOutRuuviTagAlert from '../components/ValveDetachOutRuuviTagAlert'
import ValveAttachOutRuuviTagInstructions from '../components/ValveAttachOutRuuviTagInstructions'
import ValveAttachInRuuviTagInstructions from '../components/ValveAttachInRuuviTagInstructions'
import { getLiftsByProjectId } from '../redux/slices/lifts'
import { getLevelsByProjectId } from '../redux/slices/levels'
import { useDispatch } from 'react-redux'
import AssetPhotoDropzone from '../components/AssetPhotoDropzone'
import { formatMacAddress } from '../utils/string'
import HeaterInstallForm from '../components/HeaterInstallForm'
import OcrScanner from '../components/ocr/OcrScanner'
import { extractFirstNumericSequence } from '../utils/ocr'
import { isRequired, mustBeString } from '../utils/validation'
import { pollValveStatus, updateValve } from '../services/valve'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { getHeater, getHeaters } from '../services/heater'
import RuuviTagScanner from '../components/installation/RuuviTagScanner'
import ValveStatus from '../types/ValveStatus'
import AccessDenied from './error/AccessDenied'
import { usePolicy } from '../contexts/PolicyContext'
import HeaterEnterCustomerReferenceInstructions from '../components/HeaterEnterCustomerReferenceInstructions'

enum InstallHeaterStep {
  SELECT_PROJECT = 0,
  SCAN_QR_CODE_OR_ENTER_CODE = 1,
  HEATER_SETUP = 2,
  IN_RUUVITAG_SETUP = 3,
  OUT_RUUVITAG_SETUP = 4,
  ASSET_SETUP = 5,
  INSTALLATION_COMPLETED = 6
}

function clampInstallHeaterStep(value: number): InstallHeaterStep {
  if (value < InstallHeaterStep.SELECT_PROJECT) {
    return InstallHeaterStep.SELECT_PROJECT
  }

  if (value > InstallHeaterStep.INSTALLATION_COMPLETED) {
    return InstallHeaterStep.INSTALLATION_COMPLETED
  }

  return value as InstallHeaterStep
}

export default function InstallHeaterPage() {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  /**
   * The policy.
   */
  const { canInstallHeater } = usePolicy()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The navigate function.
   */
  const navigate = useNavigate()

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * The URL search params.
   */
  const [searchParams, setSearchParams] = useSearchParams()

  /**
   * The location.
   */
  const location = useLocation()

  /**
   * Indicates if on mobile.
   */
  const isVerticalStepper = useMediaQuery(theme.breakpoints.down(1920))

  /**
   * The initial project ID (from query param `project`).
   */
  const initialProjectId = searchParams.has('project')
    ? parseInt(searchParams.get('project') as string)
    : undefined

  /**
   * The initial device name (from query param `device`).
   */
  const initialDeviceName = searchParams.has('device')
    ? (searchParams.get('device') as string)
    : undefined

  /**
   * The initial active step. Prefer explicit `step` query param when present.
   */
  const initialActiveStep = searchParams.has('step')
    ? (parseInt(searchParams.get('step') as string) as InstallHeaterStep)
    : initialProjectId !== undefined && initialDeviceName !== undefined
      ? InstallHeaterStep.HEATER_SETUP
      : initialProjectId !== undefined
        ? InstallHeaterStep.SCAN_QR_CODE_OR_ENTER_CODE
        : InstallHeaterStep.SELECT_PROJECT

  /**
   * The active step.
   */
  const [activeStep, setActiveStep] = useState<number>(initialActiveStep)
  const _prevDeviceNameRef = useRef<string | undefined>(undefined)
  const _initialMountRef = useRef(true)

  /**
   * The selected project.
   */
  const [selectedProject, setSelectedProject] = useState<Project>()

  /**
   * The selected project's ID.
   */
  const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>(initialProjectId)

  /**
   * The selected device.
   */
  const [selectedDevice, setSelectedDevice] = useState<Device>()

  /**
   * The selected device name.
   */
  const [selectedDeviceName, setSelectedDeviceName] = useState<string | undefined>(
    initialDeviceName
  )

  /**
   * The selected heater.
   */
  const [selectedHeater, setSelectedHeater] = useState<Heater | null>(null)

  /**
   * The selected "in" RuuviTag.
   */
  const [selectedInRuuviTag, setSelectedInRuuviTag] = useState<RuuviTag | null>(null)

  /**
   * The selected "out" RuuviTag.
   */
  const [selectedOutRuuviTag, setSelectedOutRuuviTag] = useState<RuuviTag | null>(null)

  /**
   * The asset.
   */
  const [asset, setAsset] = useState<Asset | null>(null)

  // Sync non-step state to URL search params (replace current history entry so these don't create navigation entries)
  useEffect(() => {
    // Initialize mount refs used to detect the "first device selection" case.
    if (_initialMountRef.current) {
      _initialMountRef.current = false
      // Remember the device name that was present at mount (may be undefined).
      _prevDeviceNameRef.current = selectedDeviceName
      // continue; we still want to sync URL on mount
    }

    const params = new URLSearchParams()

    // Keep the step param so Back/Refresh restore wizard step
    params.set('step', String(activeStep))

    if (selectedProjectId !== undefined) {
      params.set('project', String(selectedProjectId))
    }

    if (selectedDeviceName) {
      params.set('device', selectedDeviceName)
    }

    if (selectedHeater?.id) {
      params.set('heaterId', String(selectedHeater.id))
    }

    if (selectedInRuuviTag?.id) {
      params.set('inRuuviTagId', String(selectedInRuuviTag.id))
    }

    if (selectedOutRuuviTag?.id) {
      params.set('outRuuviTagId', String(selectedOutRuuviTag.id))
    }

    if (asset?.id) {
      params.set('asset', String(asset.id))
    }

    // Decide between replace vs push based on the current URL's `step` value.
    // Additionally, handle the special-case: when a device is first selected
    // while on the SCAN step we want to push a history entry so Back returns
    // from device summary to the enter-code view. We detect this by seeing
    // that there was no previous device name recorded and now there is one.
    const previousParams = new URLSearchParams(location.search)
    const previousStep = previousParams.get('step')

    let replace = previousStep === String(activeStep)

    if (
      activeStep === InstallHeaterStep.SCAN_QR_CODE_OR_ENTER_CODE &&
      selectedDeviceName &&
      !_prevDeviceNameRef.current
    ) {
      // Force a navigation push (replace = false) for this first selection.
      replace = false
    }

    setSearchParams(params, { replace })

    // Record the device name for future comparisons.
    _prevDeviceNameRef.current = selectedDeviceName
  }, [
    selectedProjectId,
    selectedDeviceName,
    selectedHeater?.id,
    selectedInRuuviTag?.id,
    selectedOutRuuviTag?.id,
    asset?.id,
    activeStep
  ])

  // When the search params change (e.g. user navigates back/forward), update local state.
  useEffect(() => {
    const projectParam = searchParams.get('project')
    if (projectParam) {
      const projectId = parseInt(projectParam)
      if (projectId !== selectedProjectId) {
        setSelectedProjectId(() => projectId)
      }
    }

    const deviceName = searchParams.get('device')
    if (deviceName && deviceName !== selectedDeviceName) {
      setSelectedDeviceName(() => deviceName)
    }

    const heaterId = searchParams.get('heaterId')
    if (heaterId && heaterId !== selectedHeater?.id) {
      loadSelectedHeater(heaterId)
    }

    const inRuuviTagId = searchParams.get('inRuuviTagId')
    if (inRuuviTagId && inRuuviTagId !== selectedInRuuviTag?.id) {
      loadSelectedInRuuviTag(inRuuviTagId)
    }

    const outRuuviTagId = searchParams.get('outRuuviTagId')
    if (outRuuviTagId && outRuuviTagId !== selectedOutRuuviTag?.id) {
      loadSelectedOutRuuviTag(outRuuviTagId)
    }

    const assetId = searchParams.get('asset')
    if (assetId && assetId !== asset?.id) {
      loadAsset(assetId)
    }
  }, [searchParams])

  /**
   * Handle the next event.
   */
  function handleNext() {
    setActiveStep((currentStep) => {
      const next =
        currentStep === InstallHeaterStep.SELECT_PROJECT && initialDeviceName !== undefined
          ? InstallHeaterStep.HEATER_SETUP
          : clampInstallHeaterStep(currentStep + 1)

      // URL will be synced from state by the central effect above.

      return next
    })
  }

  /**
   * Handle the previous event.
   */
  function handlePrevious() {
    if (activeStep === InstallHeaterStep.SELECT_PROJECT) {
      navigate('/install')
      return
    }

    setActiveStep((currentStep) => {
      const prev = clampInstallHeaterStep(currentStep - 1)

      // URL will be synced from state by the central effect above.

      return prev
    })
  }

  function handleExit() {
    setActiveStep(() => InstallHeaterStep.SELECT_PROJECT)
    setSelectedProject(undefined)
    setSelectedProjectId(undefined)
    setSelectedDevice(undefined)
    setSelectedDeviceName(undefined)
    setSelectedHeater(null)
    setSelectedInRuuviTag(null)
    setSelectedOutRuuviTag(null)
    setAsset(null)
    navigate('/install')
  }

  /**
   * Handle the "after submit" event.
   */
  function handleAfterSubmit(asset: Asset) {
    setAsset(() => asset)
  }

  function handleInRuuviTagChange(ruuviTag: RuuviTag | null) {
    if (ruuviTag !== null && ruuviTag.id === selectedOutRuuviTag?.id) {
      dispatch(createErrorNotification(t('valveInRuuviTagCannotBeSameAsValveOutRuuviTag')))
      setSelectedInRuuviTag(null)
    } else {
      setSelectedInRuuviTag(ruuviTag)
    }
  }

  function handleOutRuuviTagChange(ruuviTag: RuuviTag | null) {
    if (ruuviTag !== null && ruuviTag.id === selectedInRuuviTag?.id) {
      dispatch(createErrorNotification(t('valveOutRuuviTagCannotBeSameAsValveInRuuviTag')))
      setSelectedOutRuuviTag(null)
    } else {
      setSelectedOutRuuviTag(ruuviTag)
    }
  }

  /**
   * Set the installation as completed when the asset is set to search params.
   */
  useEffect(() => {
    const assetId = searchParams.get('asset')
    if (assetId) {
      setActiveStep(() => InstallHeaterStep.INSTALLATION_COMPLETED)
    }
  }, [searchParams])

  /**
   * Checks if the step is completed.
   */
  function isStepCompleted(step: InstallHeaterStep): boolean {
    if (step === InstallHeaterStep.INSTALLATION_COMPLETED) {
      return activeStep >= InstallHeaterStep.INSTALLATION_COMPLETED
    }

    return activeStep > step
  }

  /**
   * Load the selected project.
   */
  async function loadSelectedProject(id: number, controller?: AbortController) {
    const project = await getProject(id, { signal: controller?.signal })
    setSelectedProject(() => project)
  }

  /**
   * Load the selected device.
   */
  async function loadSelectedDevice(name: string, controller?: AbortController) {
    const device = await getDevice(name, { signal: controller?.signal })

    if (device.type === DeviceType.VALVE) {
      setSelectedDevice(device)
    } else {
      setSelectedDeviceName(undefined)
    }
  }

  /**
   * Load the selected heater.
   */
  async function loadSelectedHeater(id: string, controller?: AbortController) {
    setSelectedHeater(await getHeater(id, { signal: controller?.signal }))
  }

  /**
   * Load the selected "in" RuuviTag.
   */
  async function loadSelectedInRuuviTag(id: string, controller?: AbortController) {
    setSelectedInRuuviTag(await getRuuviTag(id, { signal: controller?.signal }))
  }

  /**
   * Load the selected "out" RuuviTag.
   */
  async function loadSelectedOutRuuviTag(id: string, controller?: AbortController) {
    setSelectedOutRuuviTag(await getRuuviTag(id, { signal: controller?.signal }))
  }

  /**
   * Load the asset.
   */
  async function loadAsset(id: string, controller?: AbortController) {
    setAsset(await getAsset(id, { signal: controller?.signal }))
  }

  /**
   * Load the selected project when the ID of the selected project changes.
   */
  useEffect(() => {
    if (selectedProjectId) {
      const controller = new AbortController()

      loadSelectedProject(selectedProjectId, controller)

      return () => controller.abort()
    } else {
      setSelectedProject(undefined)
    }
  }, [selectedProjectId])

  /**
   * Load the selected device when the name of the selected device changes.
   */
  useEffect(() => {
    if (selectedDeviceName) {
      const controller = new AbortController()

      loadSelectedDevice(selectedDeviceName, controller)

      return () => controller.abort()
    } else {
      setSelectedDevice(undefined)
    }
  }, [selectedDeviceName])

  /**
   * Initialize heater and RuuviTags when the selected device changes.
   */
  useEffect(() => {
    if (selectedDevice) {
      const controller = new AbortController()

      if (selectedHeater === null && selectedDevice?.heater) {
        setSelectedHeater(selectedDevice.heater)
      } else if (selectedHeater === null && selectedDevice.heaterId) {
        loadSelectedHeater(selectedDevice.heaterId, controller)
      }

      if (selectedInRuuviTag === null && selectedDevice?.inRuuviTag) {
        setSelectedInRuuviTag(selectedDevice.inRuuviTag)
      } else if (selectedInRuuviTag === null && selectedDevice.inRuuviTagId) {
        loadSelectedInRuuviTag(selectedDevice.inRuuviTagId, controller)
      }

      if (selectedOutRuuviTag === null && selectedDevice?.outRuuviTag) {
        setSelectedOutRuuviTag(selectedDevice.outRuuviTag)
      } else if (selectedOutRuuviTag === null && selectedDevice.outRuuviTagId) {
        loadSelectedOutRuuviTag(selectedDevice.outRuuviTagId, controller)
      }

      return () => controller.abort()
    }
  }, [selectedDevice])

  if (!canInstallHeater()) {
    return <AccessDenied />
  }

  return (
    <Container maxWidth={false}>
      <PageHeader
        title={t('installHeater')}
        breadcrumbs={
          <Breadcrumbs>
            <Link component={NavLink} to="/">
              {t('frontpage')}
            </Link>

            <Link component={NavLink} to="/install">
              {t('installation')}
            </Link>

            <Typography>{t('heater')}</Typography>
          </Breadcrumbs>
        }
      />

      <Stepper
        nonLinear={true}
        activeStep={activeStep}
        orientation={isVerticalStepper ? 'vertical' : 'horizontal'}
        sx={{ mt: 6, mb: 12 }}
      >
        <Step
          index={InstallHeaterStep.SELECT_PROJECT}
          key={InstallHeaterStep.SELECT_PROJECT}
          completed={isStepCompleted(InstallHeaterStep.SELECT_PROJECT)}
        >
          <StepButton>{t('selectProject')}</StepButton>
        </Step>

        <Step
          index={InstallHeaterStep.SCAN_QR_CODE_OR_ENTER_CODE}
          key={InstallHeaterStep.SCAN_QR_CODE_OR_ENTER_CODE}
          completed={isStepCompleted(InstallHeaterStep.SCAN_QR_CODE_OR_ENTER_CODE)}
        >
          <StepButton>{t('scanQrCodeOrEnterDeviceCode')}</StepButton>
        </Step>

        <Step
          index={InstallHeaterStep.HEATER_SETUP}
          key={InstallHeaterStep.HEATER_SETUP}
          completed={isStepCompleted(InstallHeaterStep.HEATER_SETUP)}
        >
          <StepButton>{t('enterHeaterDetails')}</StepButton>
        </Step>

        <Step
          index={InstallHeaterStep.IN_RUUVITAG_SETUP}
          key={InstallHeaterStep.IN_RUUVITAG_SETUP}
          completed={isStepCompleted(InstallHeaterStep.IN_RUUVITAG_SETUP)}
        >
          <StepButton>{t('installValveInRuuviTag')}</StepButton>
        </Step>

        <Step
          index={InstallHeaterStep.OUT_RUUVITAG_SETUP}
          key={InstallHeaterStep.OUT_RUUVITAG_SETUP}
          completed={isStepCompleted(InstallHeaterStep.OUT_RUUVITAG_SETUP)}
        >
          <StepButton>{t('installValveOutRuuviTag')}</StepButton>
        </Step>

        <Step
          index={InstallHeaterStep.ASSET_SETUP}
          key={InstallHeaterStep.ASSET_SETUP}
          completed={isStepCompleted(InstallHeaterStep.ASSET_SETUP)}
        >
          <StepButton>{t('enterAssetDetails')}</StepButton>
        </Step>

        <Step
          index={InstallHeaterStep.INSTALLATION_COMPLETED}
          key={InstallHeaterStep.INSTALLATION_COMPLETED}
          completed={isStepCompleted(InstallHeaterStep.INSTALLATION_COMPLETED)}
        >
          <StepButton>{t('installationCompleted')}</StepButton>
        </Step>
      </Stepper>

      <Container maxWidth="md" sx={{ px: '0 !important' }}>
        {activeStep === InstallHeaterStep.SELECT_PROJECT && (
          <SelectProjectStep
            value={selectedProjectId}
            onChange={setSelectedProjectId}
            onNext={handleNext}
            onCancel={handleExit}
          />
        )}

        {activeStep === InstallHeaterStep.SCAN_QR_CODE_OR_ENTER_CODE && (
          <ScanQrCodeOrEnterDeviceCodeStep
            type={DeviceType.VALVE}
            withDetachHeaterAlert={false}
            onChange={setSelectedDeviceName}
            onDeviceChange={setSelectedDevice}
            onNext={handleNext}
            onCancel={handleExit}
            onBack={handlePrevious}
            initialDeviceName={selectedDeviceName}
          />
        )}

        {activeStep === InstallHeaterStep.HEATER_SETUP && (
          <>
            {!selectedProject || !selectedDevice ? (
              <LoadingState />
            ) : (
              <HeaterSetup
                project={selectedProject}
                valve={selectedDevice as Valve}
                heater={selectedHeater}
                onValveChange={setSelectedDevice}
                onChange={setSelectedHeater}
                onNext={handleNext}
                onCancel={handlePrevious}
              />
            )}
          </>
        )}

        {activeStep === InstallHeaterStep.IN_RUUVITAG_SETUP && (
          <>
            {!selectedProject || !selectedDevice || !selectedHeater ? (
              <LoadingState />
            ) : (
              <RuuviTagSetup
                type={RuuviTagType.VALVE_IN}
                valve={{
                  ...(selectedDevice as Valve),
                  outRuuviTagId:
                    selectedOutRuuviTag?.id ?? (selectedDevice as Valve).outRuuviTagId ?? null
                }}
                ruuviTag={selectedInRuuviTag}
                onValveChange={setSelectedDevice}
                onRuuviTagChange={handleInRuuviTagChange}
                onCancel={handlePrevious}
                onNext={handleNext}
              />
            )}
          </>
        )}

        {activeStep === InstallHeaterStep.OUT_RUUVITAG_SETUP && (
          <>
            {!selectedProject || !selectedDevice || !selectedHeater ? (
              <LoadingState />
            ) : (
              <RuuviTagSetup
                type={RuuviTagType.VALVE_OUT}
                valve={{
                  ...(selectedDevice as Valve),
                  inRuuviTagId:
                    selectedInRuuviTag?.id ?? (selectedDevice as Valve).inRuuviTagId ?? null
                }}
                onValveChange={setSelectedDevice}
                ruuviTag={selectedOutRuuviTag}
                onRuuviTagChange={handleOutRuuviTagChange}
                onCancel={handlePrevious}
                onNext={handleNext}
              />
            )}
          </>
        )}

        {activeStep === InstallHeaterStep.ASSET_SETUP && (
          <>
            {!selectedProject ||
            !selectedDevice ||
            !selectedHeater ||
            !selectedInRuuviTag ||
            !selectedOutRuuviTag ? (
              <LoadingState />
            ) : (
              <AssetSetup
                project={selectedProject}
                valve={selectedDevice as Valve}
                heater={selectedHeater}
                inRuuviTag={selectedInRuuviTag}
                outRuuviTag={selectedOutRuuviTag}
                asset={asset}
                onCancel={handlePrevious}
                afterSubmit={handleAfterSubmit}
              />
            )}
          </>
        )}

        {activeStep === InstallHeaterStep.INSTALLATION_COMPLETED && (
          <>
            {!asset ||
            !selectedDevice ||
            !selectedHeater ||
            !selectedInRuuviTag ||
            !selectedOutRuuviTag ? (
              <LoadingState />
            ) : (
              <InstallationCompletedStep
                asset={asset}
                valve={selectedDevice as Valve}
                heater={selectedHeater}
                inRuuviTag={selectedInRuuviTag}
                outRuuviTag={selectedOutRuuviTag}
              />
            )}
          </>
        )}
      </Container>
    </Container>
  )
}

enum HeaterSetupStep {
  ENTER_SERIAL_NUMBER_OR_CUSTOMER_REFERENCE = 0,
  CREATE_HEATER = 1
}

interface HeaterSetupProps {
  project: Project
  valve: Valve
  heater?: Heater | null
  onChange: (heater: Heater | null) => void
  onValveChange: (device: Device) => void
  onNext: () => void
  onCancel: () => void
}

function HeaterSetup({
  valve,
  heater,
  onChange,
  onValveChange,
  onNext,
  onCancel
}: HeaterSetupProps) {
  /**
   * The active step.
   */
  const [activeStep, setActiveStep] = useState<HeaterSetupStep>(
    HeaterSetupStep.ENTER_SERIAL_NUMBER_OR_CUSTOMER_REFERENCE
  )

  /**
   * The form values.
   */
  const [formValues, setFormValues] = useState<Partial<HeaterInput>>({})

  /**
   * Handle the "cancel" action.
   */
  function handleCancel() {
    if (activeStep === HeaterSetupStep.ENTER_SERIAL_NUMBER_OR_CUSTOMER_REFERENCE) {
      // Reset the heater unless associated with the VALVE.
      if (valve.heaterId !== heater?.id) {
        onChange(null)
      }

      onCancel()
    } else {
      setActiveStep((previousActiveStep) => previousActiveStep - 1)
    }
  }

  /**
   * Handle the "skip" action.
   */
  function handleSkip() {
    onChange(null)
    setActiveStep((previousActiveStep) => previousActiveStep + 1)
  }

  /**
   * Handle the "next" action.
   */
  function handleNext() {
    onNext()
  }

  /**
   *
   */
  function handleCustomerReference(customerReference: string | null) {
    if (heater === null || heater === undefined) {
      setFormValues({ customerReference })
      setActiveStep(HeaterSetupStep.CREATE_HEATER)
    } else {
      onNext()
    }
  }

  return (
    <>
      {activeStep === HeaterSetupStep.ENTER_SERIAL_NUMBER_OR_CUSTOMER_REFERENCE && (
        <EnterCustomerReference
          valve={valve}
          heater={heater}
          onValveChange={onValveChange}
          onHeaterChange={onChange}
          onCancel={handleCancel}
          onNext={handleCustomerReference}
        />
      )}

      {activeStep === HeaterSetupStep.CREATE_HEATER && (
        <CreateHeater
          valve={valve}
          heater={heater}
          formValues={formValues}
          onChange={onChange}
          onCancel={handleCancel}
          onNext={handleNext}
        />
      )}
    </>
  )
}

interface EnterSerialNumberOrCustomerReferenceProps {
  valve: Valve
  heater?: Heater | null
  onChange: (heater: Heater | null) => void
  onValveChange: (valve: Valve) => void
  onCancel: () => void
  onSkip: () => void
  onNext: () => void
}

/** @deprecated */
function EnterSerialNumberOrCustomerReference({
  valve,
  heater,
  onChange,
  onValveChange,
  onCancel,
  onSkip,
  onNext
}: EnterSerialNumberOrCustomerReferenceProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  /**
   * Indicates if the detach heater alert should be shown.
   */
  const [showDetachHeaterAlert, setShowDetachHeaterAlert] = useState<boolean>(true)

  /**
   * Indicates if the heater is attached to this VALVE.
   */
  const isHeaterAttachedToThisValve = heater?.deviceName === valve.name

  /**
   * Indicates if the heater is attached to some VALVE.
   */
  const isHeaterAttachedToSomeValve = heater?.deviceName !== null

  /**
   * Handle heater after the update.
   */
  function handleAfterUpdate(updatedHeater: Heater) {
    if (heater?.deviceName === valve.name && updatedHeater.deviceName === null) {
      onValveChange({ ...valve, heaterId: null, heater: null })
    }

    onChange(updatedHeater)
  }

  /**
   * Handle the "change" event.
   */
  function handleChange(newHeater: Heater | null) {
    if (newHeater?.id !== heater?.id) {
      setShowDetachHeaterAlert(true)
    }

    onChange(newHeater)
  }

  return (
    <Card>
      <CardHeader title={t('enterHeaterCustomerReference')} />

      <CardContent sx={{ pt: 0 }}>
        {!isHeaterAttachedToThisValve && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle sx={{ m: 0 }}>
                {t('youMaySkipThisStepIfYouWantToCreateNewHeater')}
              </AlertTitle>
            </Alert>

            <HeaterAutocomplete onChange={handleChange} />
          </>
        )}

        {heater && (
          <>
            {isHeaterAttachedToThisValve && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <AlertTitle sx={{ mt: 0 }}>{t('heaterIsAlreadyAttachedToThisValve')}</AlertTitle>
              </Alert>
            )}

            <HeaterDetailsList heater={heater} />

            {showDetachHeaterAlert && isHeaterAttachedToSomeValve && (
              <HeaterDetachAlert
                severity={isHeaterAttachedToThisValve ? 'info' : 'warning'}
                title={
                  isHeaterAttachedToThisValve
                    ? t('doYouWantToDetachHeaterFromThisValve')
                    : undefined
                }
                heater={heater}
                afterUpdate={handleAfterUpdate}
                onCancel={() => setShowDetachHeaterAlert(false)}
              />
            )}
          </>
        )}
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="text" color="inherit" onClick={onCancel}>
          {t('cancel')}
        </Button>

        <Button
          variant="outlined"
          color="primary"
          disabled={isHeaterAttachedToThisValve}
          onClick={onSkip}
        >
          {t('skip')}
        </Button>

        <Button
          variant="contained"
          color="primary"
          disabled={heater === undefined || heater === null}
          onClick={onNext}
        >
          {t('next')}
        </Button>
      </CardActions>
    </Card>
  )
}

interface EnterCustomerReferenceProps {
  valve: Valve
  onValveChange: (valve: Valve) => void
  heater?: Heater | null
  onHeaterChange: (heater: Heater | null) => void
  onCancel: () => void
  onNext: (customerReference: string | null) => void
}

function EnterCustomerReference({
  valve,
  onValveChange,
  heater,
  onHeaterChange,
  onCancel,
  onNext
}: EnterCustomerReferenceProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  /**
   * The dispatch.
   */
  const dispatch = useDispatch()

  /**
   * Indicates if the detach heater alert should be shown.
   */
  const [showDetachHeaterAlert, setShowDetachHeaterAlert] = useState<boolean>(true)

  /**
   * The customer reference.
   */
  const [customerReference, setCustomerReference] = useState<string>('')

  /**
   * The loading state.
   */
  const [isLoading, setLoading] = useState<boolean>(false)

  /**
   * When true, OCR has a numeric candidate and we should hide the input field.
   */
  const [isConfirmingScan, setIsConfirmingScan] = useState(false)

  /**
   * Indicates if the heater is attached to this VALVE.
   */
  const isHeaterAttachedToThisValve = heater?.deviceName === valve.name

  /**
   * Indicates if the heater is attached to some VALVE.
   */
  const isHeaterAttachedToSomeValve = heater?.deviceName !== null

  /**
   * Handle heater after the update.
   */
  function handleAfterUpdate(updatedHeater: Heater) {
    if (heater?.deviceName === valve.name && updatedHeater.deviceName === null) {
      onValveChange({ ...valve, heaterId: null, heater: null })
      onHeaterChange(null)
    }
  }

  async function handleNext() {
    if (heater) {
      onNext(heater.customerReference)
    } else if (customerReference.length > 0) {
      handleSearchHeaterByCustomerRefrence(customerReference)
    } else {
      onNext(null)
    }
  }

  async function handleSearchHeaterByCustomerRefrence(customerReference: string) {
    try {
      setLoading(true)

      const heaters = await getHeaters({ customerReference })

      if (heaters.length) {
        // FIXME: What if multiple heaters are found?
        onHeaterChange(heaters[0])
      } else {
        onNext(customerReference)
      }
    } catch {
      dispatch(createErrorNotification(t('heater:loadHeatersError')))
    } finally {
      setLoading(false)
    }
  }

  function handleSkip() {
    if (!heater) {
      onNext(null)
    } else {
      onNext(heater.customerReference)
    }
  }

  return (
    <Card>
      <CardHeader title={heater ? t('heaterDetails') : t('enterHeaterCustomerReference')} />

      <CardContent sx={{ pt: 0 }}>
        {!heater && (
          <>
            <HeaterEnterCustomerReferenceInstructions />

            {/*<HeaterAutocomplete onChange={handleChange} />*/}
            <Stack direction="row" spacing={2} alignItems="flex-start">
              {!isConfirmingScan && (
                <TextField
                  fullWidth
                  label={t('heater:customerReference')}
                  value={customerReference}
                  onChange={(event) => setCustomerReference(event.target.value.trim())}
                  variant="outlined"
                  sx={{ flex: 1 }}
                />
              )}

              <OcrScanner
                label={t('scan')}
                disabled={isLoading}
                onConfirming={(isConfirming) => setIsConfirmingScan(isConfirming)}
                onResult={(text) => {
                  const digits = extractFirstNumericSequence(text)
                  if (digits) {
                    // set the customer reference and immediately proceed to the next step
                    const trimmed = digits.trim()
                    setCustomerReference(trimmed)
                    // directly search and advance using the same logic as the Next button
                    handleSearchHeaterByCustomerRefrence(trimmed)
                  } else {
                    dispatch(createErrorNotification(t('installation:noDigitsFound')))
                  }
                }}
              />
            </Stack>
          </>
        )}

        {heater && (
          <>
            {isHeaterAttachedToThisValve && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <AlertTitle sx={{ mt: 0 }}>{t('heaterIsAlreadyAttachedToThisValve')}</AlertTitle>
              </Alert>
            )}

            <HeaterDetailsList heater={heater} />

            {showDetachHeaterAlert && isHeaterAttachedToSomeValve && (
              <HeaterDetachAlert
                severity={isHeaterAttachedToThisValve ? 'info' : 'warning'}
                title={
                  isHeaterAttachedToThisValve
                    ? t('doYouWantToDetachHeaterFromThisValve')
                    : undefined
                }
                heater={heater}
                afterUpdate={handleAfterUpdate}
                onCancel={() => setShowDetachHeaterAlert(false)}
              />
            )}
          </>
        )}
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="text" color="inherit" onClick={onCancel}>
          {t('cancel')}
        </Button>

        {!heater && (
          <Button variant="text" color="primary" onClick={handleSkip}>
            {t('skip')}
          </Button>
        )}

        <LoadingButton variant="contained" color="primary" loading={isLoading} onClick={handleNext}>
          {t('next')}
        </LoadingButton>
      </CardActions>
    </Card>
  )
}

interface CreateHeaterProps {
  valve: Valve
  heater?: Heater | null
  formValues?: Partial<HeaterInput>
  onChange: (heater: Heater | null) => void
  onCancel: () => void
  onNext: () => void
}

function CreateHeater({ heater, formValues = {}, onChange, onCancel, onNext }: CreateHeaterProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  /**
   * Handle the event after submit.
   */
  async function handleAfterSubmit(heater: Heater) {
    onChange(heater)
    onNext()
  }

  return (
    <Card>
      <CardHeader title={t('createNewHeater')} />
      <CardContent sx={{ pt: 0 }}>
        <Box sx={{ mt: 2 }}>
          <HeaterInstallForm
            heater={heater}
            formValues={formValues}
            onCancel={onCancel}
            afterSubmit={handleAfterSubmit}
            submitButtonText={t('next')}
          />
        </Box>
      </CardContent>
    </Card>
  )
}

enum RuuviTagSetupStep {
  ACTIVATE_RUUVITAG = 0,
  ATTACH_RUUVITAG_TO_HEATER = 1
}

interface RuuviTagSetupProps {
  type: RuuviTagType.VALVE_IN | RuuviTagType.VALVE_OUT
  valve: Valve
  ruuviTag?: RuuviTag | null
  cancelButtonText?: string
  nextButtonText?: string
  onValveChange: (valve: Valve) => void
  onRuuviTagChange: (ruuviTag: RuuviTag | null) => void
  onCancel: () => void
  onNext: () => void
}

export function RuuviTagSetup({
  type,
  valve,
  ruuviTag,
  cancelButtonText,
  nextButtonText,
  onValveChange,
  onRuuviTagChange,
  onCancel,
  onNext
}: RuuviTagSetupProps) {
  /**
   * The active step state.
   */
  const [activeStep, setActiveStep] = useState<RuuviTagSetupStep>(
    RuuviTagSetupStep.ACTIVATE_RUUVITAG
  )

  /**
   * Handle the "cancel" event.
   */
  function handleCancel() {
    if (activeStep === RuuviTagSetupStep.ACTIVATE_RUUVITAG) {
      if (
        (type === RuuviTagType.VALVE_IN && ruuviTag?.id !== valve.inRuuviTagId) ||
        (type === RuuviTagType.VALVE_OUT && ruuviTag?.id !== valve.outRuuviTagId)
      ) {
        onRuuviTagChange(null)
      }

      onCancel()
    } else {
      setActiveStep(RuuviTagSetupStep.ACTIVATE_RUUVITAG)
    }
  }

  /**
   * Handle the "next" event.
   */
  function handleNext() {
    if (activeStep === RuuviTagSetupStep.ATTACH_RUUVITAG_TO_HEATER) {
      onNext()
    } else {
      setActiveStep((previousActiveStep) => previousActiveStep + 1)
    }
  }

  return (
    <>
      {activeStep === RuuviTagSetupStep.ACTIVATE_RUUVITAG && (
        <>
          {ruuviTag ? (
            <RuuviTagDetailsStep
              type={type}
              valve={valve}
              onValveChange={onValveChange}
              ruuviTag={ruuviTag}
              onRuuviTagChange={onRuuviTagChange}
              onCancel={handleCancel}
              onNext={handleNext}
            />
          ) : (
            <ActivateRuuviTagStep
              type={type}
              valve={valve}
              onValveChange={onValveChange}
              ruuviTag={ruuviTag ?? null}
              onRuuviTagChange={onRuuviTagChange}
              onCancel={handleCancel}
              onNext={handleNext}
            />
          )}
        </>
      )}

      {activeStep === RuuviTagSetupStep.ATTACH_RUUVITAG_TO_HEATER && (
        <>
          {ruuviTag ? (
            <AttachRuuviTagToHeaterStep
              type={type}
              valve={valve}
              ruuviTag={ruuviTag}
              onCancel={handleCancel}
              onNext={handleNext}
            />
          ) : (
            <LoadingState />
          )}
        </>
      )}
    </>
  )
}
interface ActivateRuuviTagStepProps {
  type: RuuviTagType
  valve: Valve
  onValveChange: (valve: Valve) => void
  ruuviTag: RuuviTag | null
  onRuuviTagChange: (ruuviTag: RuuviTag | null) => void
  onCancel: () => void
  onNext: () => void
}

function ActivateRuuviTagStep({
  type,
  valve,
  ruuviTag,
  onRuuviTagChange,
  onCancel,
  onNext
}: ActivateRuuviTagStepProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  /**
   * Handle the "cancel" action.
   */
  function handleCancel() {
    // Reset the RuuviTag unless associated with the VALVE.
    if (
      (type === RuuviTagType.VALVE_IN && ruuviTag?.id !== valve.inRuuviTagId) ||
      (type === RuuviTagType.VALVE_OUT && ruuviTag?.id !== valve.outRuuviTagId)
    ) {
      onRuuviTagChange(null)
    }

    onCancel()
  }

  function handleChange(ruuviTag: RuuviTag | null) {
    onRuuviTagChange(ruuviTag)
  }

  return (
    <>
      <Card>
        <CardHeader
          title={
            type === RuuviTagType.VALVE_IN
              ? t('attachValveInRuuviTagToValve')
              : t('attachValveOutRuuviTagToValve')
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <RuuviTagScanner
            type={type}
            device={valve}
            allowScan={valve.status}
            onChange={handleChange}
            onCancel={onCancel}
          />
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <Button variant="text" color="inherit" onClick={handleCancel}>
            {t('cancel')}
          </Button>

          <Button variant="contained" color="primary" disabled={true} onClick={onNext}>
            {t('next')}
          </Button>
        </CardActions>
      </Card>
    </>
  )
}

interface SearchRuuviTagsStepProps {
  type: RuuviTagType.VALVE_IN | RuuviTagType.VALVE_OUT
  valve: Valve
  ruuviTag?: RuuviTag | null
  onValveChange?: (valve: Valve) => void
  onRuuviTagChange: (ruuviTag: RuuviTag | null) => void
  onNext: () => void
  onCancel: () => void
}

function SearchRuuviTagsStep({
  type,
  valve,
  onValveChange,
  ruuviTag,
  onRuuviTagChange,
  onNext,
  onCancel
}: SearchRuuviTagsStepProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  /**
   * Indicates if the RuuviTag is attached to this VALVE.
   */
  const isRuuviTagAttachedToThisValve =
    (type === RuuviTagType.VALVE_IN && ruuviTag?.id === valve.inRuuviTagId) ||
    (type === RuuviTagType.VALVE_OUT && ruuviTag?.id === valve.outRuuviTagId)

  /**
   * The search interval ref.
   */
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * The search timeout ref.
   */
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * Indicates if the pairing is in progress.
   */
  const [isPairing, setIsPairing] = useState<boolean>(false)

  /**
   * Indicates if the tag has been paired.
   */
  const [isPaired, setIsPaired] = useState<boolean>(false)

  /**
   * Indicates if the alert should be shown.
   */
  const [showSearchAgainAlert, setShowSearchAgainAlert] = useState<boolean>(true)

  /**
   * Indicates if the alert should be shown.
   */
  const [showValveDetachAlert, setShowValveDetachAlert] = useState<boolean>(true)

  /**
   * Indicates if the alert should be shown.
   */
  const [showRuuviTagDetachAlert, setShowRuuviTagDetachAlert] = useState<boolean>(true)

  /**
   * Load the paired ruuvi tag.
   */
  const loadPairedRuuviTagOrFail = async (deviceName: string, controller?: AbortController) => {
    setIsPaired(false)
    onRuuviTagChange(null)

    const ruuviTag = await getPairedRuuviTag(deviceName, { signal: controller?.signal })

    onRuuviTagChange(ruuviTag)
    setIsPaired(true)
  }

  /**
   * Handle the cancel action.
   */
  const handleCancel = () => {
    onRuuviTagChange(null)
    onCancel()
  }

  /**
   * Handle the next action.
   */
  const handleNext = () => {
    if (ruuviTag) {
      onRuuviTagChange(ruuviTag)
      onNext()
    }
  }

  /**
   * Handle the pair event.
   */
  const handlePair = async (controller?: AbortController) => {
    setIsPairing(true)

    // Invoke action for the device.
    await invokeAction(
      valve.name,
      {
        ruuvi_pairing: 60
      },
      {
        signal: controller?.signal
      }
    )

    // Poll for the paired ruuvi tag every 5 seconds.
    const FIVE_SECONDS = 5000
    const interval = setInterval(async () => {
      if (!isPaired) {
        try {
          await loadPairedRuuviTagOrFail(valve.name, controller)
          setIsPairing(false)
          clearInterval(interval)
        } catch (_) {
          // Do nothing if failed.
        }
      }
    }, FIVE_SECONDS)
    intervalRef.current = interval

    // Clear the interval after one minute.
    const ONE_MINUTE = 60 * 1000
    const timeout = setTimeout(() => {
      setIsPairing(false)
      clearInterval(interval)
    }, ONE_MINUTE)
    timeoutRef.current = timeout
  }

  useEffect(() => {
    const controller = new AbortController()

    handlePair(controller)

    return () => {
      controller.abort()

      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <Card>
      {isPairing ? (
        <>
          <CardHeader title={t('searchingRuuviTags')} />
          <CardContent>
            <LoadingState />
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <Button variant="text" color="inherit" onClick={handleCancel}>
              {t('cancel')}
            </Button>

            <Button variant="contained" color="primary" disabled>
              {t('next')}
            </Button>
          </CardActions>
        </>
      ) : (
        <>
          <CardHeader title={t('ruuviTagDetails')} />
          <CardContent>
            {ruuviTag ? (
              <>
                <Stack direction="column" gap={2}>
                  <Alert color="success">
                    <AlertTitle sx={{ m: 0 }}>{t('ruuviTagWasFound')}</AlertTitle>
                  </Alert>

                  {isRuuviTagAttachedToThisValve && (
                    <Alert severity="success">
                      <AlertTitle sx={{ m: 0 }}>
                        {type === RuuviTagType.VALVE_IN
                          ? t('inRuuviTagIsAlreadyAttachedToThisValve')
                          : t('outRuuviTagIsAlreadyAttachedToThisValve')}
                      </AlertTitle>
                    </Alert>
                  )}
                </Stack>

                <RuuviTagDetailsList ruuviTag={ruuviTag} />

                <Stack direction="column" gap={2}>
                  {showValveDetachAlert && (
                    <>
                      {type === RuuviTagType.VALVE_IN && valve.inRuuviTagId && (
                        <ValveDetachInRuuviTagAlert
                          valve={valve}
                          severity={isRuuviTagAttachedToThisValve ? 'info' : 'warning'}
                          onValveChange={onValveChange}
                          onCancel={() => setShowValveDetachAlert(false)}
                        />
                      )}

                      {type === RuuviTagType.VALVE_OUT && valve.outRuuviTagId && (
                        <ValveDetachOutRuuviTagAlert
                          valve={valve}
                          severity={isRuuviTagAttachedToThisValve ? 'info' : 'warning'}
                          onValveChange={onValveChange}
                          onCancel={() => setShowValveDetachAlert(false)}
                        />
                      )}
                    </>
                  )}

                  {showRuuviTagDetachAlert && ruuviTag?.asset?.projectId && (
                    <RuuviTagDetachAlert
                      ruuviTag={ruuviTag}
                      onChange={onRuuviTagChange}
                      onCancel={() => setShowRuuviTagDetachAlert(false)}
                    />
                  )}

                  {showSearchAgainAlert && (
                    <Alert severity="info">
                      <AlertTitle sx={{ m: 0 }}>{t('doYouWantToSearchAgain')}</AlertTitle>
                      <Stack direction="row" alignItems="center" gap={2} sx={{ mt: 1 }}>
                        <Button
                          variant="outlined"
                          color="inherit"
                          size="small"
                          onClick={() => handlePair()}
                        >
                          {t('yes')}
                        </Button>

                        <Button
                          variant="outlined"
                          color="inherit"
                          size="small"
                          onClick={() => setShowSearchAgainAlert(false)}
                        >
                          {t('nope')}
                        </Button>
                      </Stack>
                    </Alert>
                  )}
                </Stack>
              </>
            ) : (
              <>
                {showSearchAgainAlert && (
                  <Alert severity="info">
                    <AlertTitle sx={{ m: 0 }}>
                      {t('ruuviTagsWereNotFoundDoYouWantToSearchAgain')}
                    </AlertTitle>
                    <Stack direction="row" alignItems="center" gap={2} sx={{ mt: 1 }}>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        onClick={() => handlePair()}
                      >
                        {t('yes')}
                      </Button>

                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        onClick={() => setShowSearchAgainAlert(false)}
                      >
                        {t('no')}
                      </Button>
                    </Stack>
                  </Alert>
                )}
              </>
            )}
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <Button variant="text" color="inherit" onClick={handleCancel}>
              {t('cancel')}
            </Button>

            <Button
              variant="contained"
              color="primary"
              disabled={Boolean(!ruuviTag)}
              onClick={handleNext}
            >
              {t('next')}
            </Button>
          </CardActions>
        </>
      )}
    </Card>
  )
}

interface RuuviTagDetailsStepProps {
  type: RuuviTagType.VALVE_IN | RuuviTagType.VALVE_OUT
  valve: Valve
  ruuviTag: RuuviTag
  cancelButtonText?: string
  nextButtonText?: string
  onValveChange: (valve: Valve) => void
  onRuuviTagChange: (ruuviTag: RuuviTag | null) => void
  onCancel: () => void
  onNext: () => void
}

function RuuviTagDetailsStep({
  type,
  valve,
  ruuviTag,
  cancelButtonText,
  nextButtonText,
  onValveChange,
  onRuuviTagChange,
  onCancel,
  onNext
}: RuuviTagDetailsStepProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  /**
   * Indicates if the RuuviTag is installed to this VALVE.
   */
  const isRuuviTagAttachedToThisValve =
    (type === RuuviTagType.VALVE_IN && ruuviTag?.id === valve.inRuuviTagId) ||
    (type === RuuviTagType.VALVE_OUT && ruuviTag?.id === valve.outRuuviTagId)

  const currentValveTagId =
    type === RuuviTagType.VALVE_IN ? valve.inRuuviTagId : valve.outRuuviTagId

  /**
   * Indicate if the alert should be shown.
   */
  const [showRuuviTagDetachAlert, setShowRuuviTagDetachAlert] = useState<boolean>(true)

  /**
   * Indicates if the alert should be shown.
   */
  const [showValveDetachAlert, setShowValveDetachAlert] = useState<boolean>(true)

  /**
   * Handle the cancel action.
   */
  function handleCancel() {
    if (ruuviTag && ruuviTag.id !== currentValveTagId) {
      onRuuviTagChange(null)
    }

    onCancel()
  }

  /**
   * Reset the local wizard tag selection after detaching the currently configured valve tag.
   */
  function handleValveTagDetach(updatedValve: Valve) {
    onValveChange(updatedValve)

    const updatedValveTagId =
      type === RuuviTagType.VALVE_IN ? updatedValve.inRuuviTagId : updatedValve.outRuuviTagId

    if (!updatedValveTagId) {
      onRuuviTagChange(null)
    }
  }

  return (
    <>
      <Card>
        <CardHeader
          title={
            type === RuuviTagType.VALVE_IN
              ? t('valveInRuuviTagDetails')
              : type === RuuviTagType.VALVE_OUT
                ? t('valveOutRuuviTagDetails')
                : t('ruuviTagDetails')
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {isRuuviTagAttachedToThisValve && (
            <Alert severity="success">
              <AlertTitle sx={{ m: 0 }}>
                {type === RuuviTagType.VALVE_IN
                  ? t('valveInRuuviTagIsAlreadyAttachedToThisValve')
                  : t('valveOutRuuviTagIsAlreadyAttachedToThisValve')}
              </AlertTitle>
            </Alert>
          )}

          <RuuviTagDetailsList ruuviTag={ruuviTag} showDevice={false} />

          <Stack direction="column" gap={2}>
            {showValveDetachAlert && (
              <>
                {type === RuuviTagType.VALVE_IN && valve?.inRuuviTagId && (
                  <ValveDetachInRuuviTagAlert
                    valve={valve}
                    severity={isRuuviTagAttachedToThisValve ? 'info' : 'warning'}
                    onValveChange={handleValveTagDetach}
                    onCancel={() => setShowValveDetachAlert(false)}
                  />
                )}

                {type === RuuviTagType.VALVE_OUT && valve?.outRuuviTagId && (
                  <ValveDetachOutRuuviTagAlert
                    valve={valve}
                    severity={isRuuviTagAttachedToThisValve ? 'info' : 'warning'}
                    onValveChange={handleValveTagDetach}
                    onCancel={() => setShowValveDetachAlert(false)}
                  />
                )}
              </>
            )}

            {showRuuviTagDetachAlert && ruuviTag?.asset?.projectId && (
              <RuuviTagDetachAlert
                ruuviTag={ruuviTag}
                onChange={onRuuviTagChange}
                onCancel={() => setShowRuuviTagDetachAlert(false)}
              />
            )}
          </Stack>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <Button variant="text" color="inherit" onClick={handleCancel}>
            {cancelButtonText ?? t('cancel')}
          </Button>

          <Button variant="contained" color="primary" onClick={onNext}>
            {nextButtonText ?? t('next')}
          </Button>
        </CardActions>
      </Card>
    </>
  )
}

interface AttachRuuviTagToHeaterStepProps {
  type: RuuviTagType.VALVE_IN | RuuviTagType.VALVE_OUT
  valve: Valve
  ruuviTag: RuuviTag
  onCancel: () => void
  onNext: () => void
}

function AttachRuuviTagToHeaterStep({ type, onCancel, onNext }: AttachRuuviTagToHeaterStepProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  return (
    <Card>
      <CardHeader
        title={
          type === RuuviTagType.VALVE_IN
            ? t('attachValveInRuuviTagToHeater')
            : t('attachValveOutRuuviTagToHeater')
        }
      />

      <CardContent sx={{ pt: 0 }}>
        {type === RuuviTagType.VALVE_IN ? (
          <ValveAttachInRuuviTagInstructions />
        ) : (
          <ValveAttachOutRuuviTagInstructions />
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="text" color="inherit" onClick={onCancel}>
          {t('cancel')}
        </Button>
        <Button variant="contained" color="primary" onClick={onNext}>
          {t('next')}
        </Button>
      </CardActions>
    </Card>
  )
}

interface AssetSetupProps {
  project: Project
  valve: Valve
  heater: Heater
  inRuuviTag: RuuviTag
  outRuuviTag: RuuviTag
  asset?: Asset | null
  beforeSubmit?: (values: AssetInput, helpers: FormikHelpers<AssetInput>) => void
  afterSubmit: (asset: Asset, helpers: FormikHelpers<AssetInput>) => void
  onCancel: () => void
}

function AssetSetup({
  project,
  valve,
  heater,
  inRuuviTag,
  outRuuviTag,
  onCancel,
  beforeSubmit,
  afterSubmit
}: AssetSetupProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The photo.
   */
  const [photo, setPhoto] = useState<File | null>(null)

  /**
   * The initial values.
   */
  const initialValues: AssetInput = {
    name: '',
    company: valve?.asset?.companyId || null,
    project: project.id || null,
    level: null,
    lift: null
  }

  /**
   * The validation schema.
   */
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .typeError(mustBeString(t('asset:name')))
      .required(isRequired(t('asset:name')))
  })

  /**
   * The formik state.
   */
  const {
    values,
    touched,
    errors,
    isSubmitting,
    handleSubmit: onSubmit,
    handleBlur,
    handleChange
  } = useFormik<AssetInput>({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit
  })

  /**
   * Check if the field has an error.
   */
  function isError(field: keyof AssetInput): boolean {
    return Boolean(touched[field] && errors[field])
  }

  /**
   * Get the helper text for the field.
   */
  function getHelperText(field: keyof AssetInput): string | undefined {
    return touched[field] ? errors[field] : undefined
  }

  /**
   * Handle the "submit" action.
   */
  async function handleSubmit(
    values: AssetInput,
    { setSubmitting, ...helpers }: FormikHelpers<AssetInput>
  ) {
    try {
      setSubmitting(true)

      beforeSubmit?.(values, { setSubmitting, ...helpers })

      // Update VALVE to associate the heater and RuuviTags.
      const updatedValve = await updateValve(valve.name, {
        heater: heater.id,
        inRuuviTag: inRuuviTag.id,
        outRuuviTag: outRuuviTag.id
      })

      // Create or update the asset.
      let createdOrUpdatedAsset =
        valve?.assetId === null
          ? await createAsset({
              type: AssetType.DEVICE,
              ...values
            })
          : await updateAsset(valve.assetId, values)

      // Upload the photo if provided.
      if (photo) {
        createdOrUpdatedAsset = await uploadAssetPhoto(createdOrUpdatedAsset.id, photo)
      }

      dispatch(createSuccessNotification(t('installSuccess')))
      setSubmitting(false)
      afterSubmit?.(createdOrUpdatedAsset, { setSubmitting, ...helpers })
    } catch (_) {
      dispatch(createErrorNotification(t('installError')))
      setSubmitting(false)
    }
  }

  /**
   * Load the levels.
   */
  function loadLevels(projectId: number, controller?: AbortController) {
    dispatch(getLevelsByProjectId(projectId, { signal: controller?.signal }))
  }

  /**
   * Load the lifts.
   */
  function loadLifts(projectId: number, controller?: AbortController) {
    dispatch(getLiftsByProjectId(projectId, { signal: controller?.signal }))
  }

  /**
   * Load levels and lifts when the project changes.
   */
  useEffect(() => {
    if (project?.id) {
      const controller = new AbortController()

      loadLevels(project.id, controller)
      loadLifts(project.id, controller)

      return () => {
        controller.abort()
      }
    }
  }, [project?.id])

  return (
    <Card>
      <CardHeader title={t('enterAssetDetails')} />
      <CardContent>
        <form onSubmit={onSubmit}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label={t('asset:name')}
                value={values.name}
                error={isError('name')}
                helperText={getHelperText('name')}
                disabled={isSubmitting}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <LevelSelect
                name="level"
                label={t('asset:level')}
                project={project}
                value={values.level}
                error={isError('level')}
                helperText={getHelperText('level')}
                disabled={isSubmitting}
                withAddOption={true}
                withClearSelection={true}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <LiftSelect
                project={project}
                name="lift"
                label={t('asset:lift')}
                value={values.lift}
                error={isError('lift')}
                helperText={getHelperText('lift')}
                disabled={isSubmitting}
                withAddOption={true}
                withClearSelection={true}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            {!isSubmitting && (
              <Grid item xs={12}>
                <AssetPhotoDropzone value={photo} onChange={setPhoto} />
              </Grid>
            )}

            <Grid item xs={12}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={2}
                sx={{ mt: 6 }}
              >
                <Button variant="text" color="inherit" onClick={onCancel}>
                  {t('cancel')}
                </Button>

                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                >
                  {t('install')}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  )
}

interface InstallationCompletedStepProps {
  asset: Asset
  heater: Heater
  valve: Valve
  inRuuviTag: RuuviTag
  outRuuviTag: RuuviTag
}

function InstallationCompletedStep({
  asset,
  heater,
  valve,
  inRuuviTag,
  outRuuviTag
}: InstallationCompletedStepProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('installation')

  /**
   * The valve status.
   */
  const [valveStatus, setValveStatus] = useState<ValveStatus | null>(null)

  useEffect(() => {
    const controller = new AbortController()

    loadValveStatus(valve.name, controller)

    return () => controller.abort()
  }, [valve.name])

  /**
   * Load the valve status.
   */
  async function loadValveStatus(name: string, controller: AbortController) {
    try {
      setValveStatus(await pollValveStatus(name, { signal: controller.signal }))
    } catch (_) {
      // Ignore the error for now.
    }
  }

  return (
    <Card>
      <CardHeader title={t('installationCompleted')} />
      <CardContent sx={{ pt: 0, pb: 2 }}>
        <Alert severity="success">
          <AlertTitle sx={{ m: 0 }}>{t('installSuccess')}</AlertTitle>
        </Alert>

        <Grid container>
          <Grid item xs={12}>
            <Accordion defaultExpanded sx={{ boxShadow: 0, width: '100%' }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={500}>{t('valveDetails')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List disablePadding>
                  {asset?.name && (
                    <ListItemButton>
                      <ListItemText primary={asset.name} secondary={t('asset:name')} />
                    </ListItemButton>
                  )}

                  {asset?.company?.name && (
                    <ListItemButton>
                      <ListItemText primary={asset.company.name} secondary={t('asset:company')} />
                    </ListItemButton>
                  )}

                  {asset?.project?.name && (
                    <ListItemButton>
                      <ListItemText primary={asset.project.name} secondary={t('asset:project')} />
                    </ListItemButton>
                  )}

                  {asset?.level?.name && (
                    <ListItemButton>
                      <ListItemText primary={asset.level.name} secondary={t('asset:level')} />
                    </ListItemButton>
                  )}

                  {asset?.lift?.name && (
                    <ListItemButton>
                      <ListItemText primary={asset.lift.name} secondary={t('asset:lift')} />
                    </ListItemButton>
                  )}

                  <ListItemButton>
                    <ListItemText primary={valve.name} secondary={t('device:name')} />
                  </ListItemButton>

                  <ListItemButton>
                    <ListItemText primary={valve.shortUUID} secondary={t('device:shortUUID')} />
                  </ListItemButton>

                  <ListItemButton>
                    <ListItemText
                      primary={valve.firmwareVersion}
                      secondary={t('device:firmwareVersion')}
                    />
                  </ListItemButton>

                  <ListItemButton>
                    <ListItemText
                      primary={valve.hardwareVersion}
                      secondary={t('device:hardwareVersion')}
                    />
                  </ListItemButton>
                </List>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12}>
            <Accordion defaultExpanded sx={{ boxShadow: 0, width: '100%' }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={500}>{t('valveInRuuviTagDetails')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItemButton>
                    <ListItemText
                      primary={formatMacAddress(inRuuviTag.id)}
                      secondary={t('ruuvitag:macAddress')}
                    />
                    {valveStatus && (
                      <ListItemSecondaryAction>
                        {valveStatus.in === inRuuviTag.id ? (
                          <Tooltip title={t('manufacturing:valveInRuuviTagConfigured')}>
                            <Check color="success" />
                          </Tooltip>
                        ) : (
                          <Tooltip title={t('manufacturing:inRuuviTagIsNotSameAsConfigured')}>
                            <Warning color="warning" />
                          </Tooltip>
                        )}
                      </ListItemSecondaryAction>
                    )}
                  </ListItemButton>
                </List>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12}>
            <Accordion defaultExpanded sx={{ boxShadow: 0, width: '100%' }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={500}>{t('valveOutRuuviTagDetails')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItemButton>
                    <ListItemText
                      primary={formatMacAddress(outRuuviTag.id)}
                      secondary={t('ruuvitag:macAddress')}
                    />
                    {valveStatus && (
                      <ListItemSecondaryAction>
                        {valveStatus.out === outRuuviTag.id ? (
                          <Tooltip title={t('manufacturing:valveOutRuuviTagConfigured')}>
                            <Check color="success" />
                          </Tooltip>
                        ) : (
                          <Tooltip title={t('manufacturing:outRuuviTagIsNotSameAsConfigured')}>
                            <Warning color="warning" />
                          </Tooltip>
                        )}
                      </ListItemSecondaryAction>
                    )}
                  </ListItemButton>
                </List>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12}>
            <Accordion sx={{ boxShadow: 0, width: '100%' }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={500}>{t('heaterDetails')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <HeaterDetailsList heater={heater} />
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12}>
            <Accordion sx={{ boxShadow: 0, width: '100%' }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={500}>{t('powerSupplyDetails')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List disablePadding>
                  {valve?.powerSupply?.fuse && (
                    <ListItemButton>
                      <ListItemText
                        primary={`${valve?.powerSupply?.fuse}A`}
                        secondary={t('powerSupply:fuse')}
                      />
                    </ListItemButton>
                  )}

                  {valve?.powerSupply?.inputCables && (
                    <ListItemButton>
                      <ListItemText
                        primary={valve?.powerSupply?.inputCables}
                        secondary={t('powerSupply:inputCables')}
                      />
                    </ListItemButton>
                  )}

                  {valve?.powerSupply?.catalogProduct?.name && (
                    <ListItemButton>
                      <ListItemText
                        primary={valve.powerSupply.catalogProduct.name}
                        secondary={t('powerSupply:model')}
                      />
                    </ListItemButton>
                  )}

                  {valve?.powerSupply?.catalogProduct?.manufacturer && (
                    <ListItemButton>
                      <ListItemText
                        primary={valve.powerSupply.catalogProduct.manufacturer}
                        secondary={t('powerSupply:manufacturer')}
                      />
                    </ListItemButton>
                  )}

                  {valve?.powerSupply?.catalogProduct?.manufacturerProductCode && (
                    <ListItemButton>
                      <ListItemText
                        primary={valve.powerSupply.catalogProduct.manufacturerProductCode}
                        secondary={t('powerSupply:manufacturerProductCode')}
                      />
                    </ListItemButton>
                  )}

                  {valve?.powerSupply?.serialNumber && (
                    <ListItemButton>
                      <ListItemText
                        primary={valve.powerSupply.serialNumber}
                        secondary={t('powerSupply:serialNumber')}
                      />
                    </ListItemButton>
                  )}

                  {valve?.powerSupply?.customerReference && (
                    <ListItemButton>
                      <ListItemText
                        primary={valve.powerSupply.customerReference}
                        secondary={t('powerSupply:customerReference')}
                      />
                    </ListItemButton>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="success"
          component={NavLink}
          to={`/valves/${valve.name}`}
        >
          {t('done')}
        </Button>
      </CardActions>
    </Card>
  )
}
